import { getBuildDefaultTarget } from './runtime-target';

export class WebEnvironmentError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WebEnvironmentError';
  }
}

/**
 * NOTE: All reads below use `process.env.REACT_APP_XXX` (static dot-notation)
 * intentionally. webpack DefinePlugin only replaces *static* property accesses
 * (e.g. `process.env.REACT_APP_FOO`). Dynamic access via `process.env[name]`
 * is NOT replaced and evaluates to `undefined` at runtime in the browser bundle.
 * The former `requireEnv(name: string)` helper used dynamic access and caused
 * every env check to see `undefined`, making the guard always throw → white screen.
 */

function check(value: string | undefined, name: string): string {
  const v = value?.trim();
  if (!v || v.startsWith('replace_with_')) {
    throw new WebEnvironmentError(`Missing required environment variable: ${name}`);
  }
  return v;
}

export function assertWebEnvironment(): void {
  if (getBuildDefaultTarget() !== 'staging') {
    return;
  }

  const apiUrl = check(process.env.REACT_APP_API_URL_STAGING, 'REACT_APP_API_URL_STAGING').replace(
    /\/$/,
    '',
  );
  if (apiUrl !== 'https://staging-api.autopilotee.com') {
    throw new WebEnvironmentError(
      'Staging build requires REACT_APP_API_URL_STAGING=https://staging-api.autopilotee.com/',
    );
  }

  const webUrl = check(process.env.REACT_APP_WEB_URL_STAGING, 'REACT_APP_WEB_URL_STAGING').replace(
    /\/$/,
    '',
  );
  if (webUrl !== 'https://staging.autopilotee.com') {
    throw new WebEnvironmentError(
      'Staging build requires REACT_APP_WEB_URL_STAGING=https://staging.autopilotee.com',
    );
  }

  check(process.env.REACT_APP_COGNITO_USER_POOL_ID, 'REACT_APP_COGNITO_USER_POOL_ID');
  check(process.env.REACT_APP_COGNITO_CLIENT_ID, 'REACT_APP_COGNITO_CLIENT_ID');

  // Validate Cognito Hosted UI domain format
  const cognitoDomain = check(process.env.REACT_APP_COGNITO_DOMAIN, 'REACT_APP_COGNITO_DOMAIN')
    .replace(/^https?:\/\//i, '')
    .replace(/\/.*$/, '');
  if (!cognitoDomain.includes('.auth.') || !cognitoDomain.endsWith('.amazoncognito.com')) {
    throw new WebEnvironmentError('REACT_APP_COGNITO_DOMAIN must be a Cognito hosted UI domain');
  }

  if (process.env.REACT_APP_COGNITO_CLIENT_SECRET?.trim()) {
    throw new WebEnvironmentError('Staging SPA must not define REACT_APP_COGNITO_CLIENT_SECRET');
  }

  const stripeKey = check(process.env.REACT_APP_STRIPE_PUBLIC_KEY, 'REACT_APP_STRIPE_PUBLIC_KEY');
  if (!stripeKey.startsWith('pk_test_') || stripeKey === 'pk_test_') {
    throw new WebEnvironmentError('Staging build requires a real Stripe test publishable key');
  }

  // Firebase — each key read statically so DefinePlugin can replace them
  check(process.env.REACT_APP_FIREBASE_API_KEY, 'REACT_APP_FIREBASE_API_KEY');
  check(process.env.REACT_APP_FIREBASE_AUTH_DOMAIN, 'REACT_APP_FIREBASE_AUTH_DOMAIN');
  check(process.env.REACT_APP_FIREBASE_PROJECT_ID, 'REACT_APP_FIREBASE_PROJECT_ID');
  check(process.env.REACT_APP_FIREBASE_STORAGE_BUCKET, 'REACT_APP_FIREBASE_STORAGE_BUCKET');
  check(
    process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
  );
  check(process.env.REACT_APP_FIREBASE_APP_ID, 'REACT_APP_FIREBASE_APP_ID');
  check(process.env.REACT_APP_FIREBASE_VAPID_KEY, 'REACT_APP_FIREBASE_VAPID_KEY');

  if (
    check(process.env.REACT_APP_SENTRY_ENVIRONMENT, 'REACT_APP_SENTRY_ENVIRONMENT') !== 'staging'
  ) {
    throw new WebEnvironmentError('Staging build requires REACT_APP_SENTRY_ENVIRONMENT=staging');
  }
}
